import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";

const navLinks = [
  { label: "Erlebnisse", href: "#entertainment" },
  { label: "Events", href: "#events" },
  { label: "Preise", href: "#pricing" },
  { label: "Standort", href: "#location" },
];

export default function Navigation() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const goTo = (href) => {
    setMenuOpen(false);
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <>
      <motion.nav
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.7, ease: "easeOut" }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled
            ? "bg-navy-deep/95 xl:bg-navy-deep/90 xl:backdrop-blur-xl border-b border-gold/10 shadow-[0_4px_30px_rgba(0,0,0,0.4)]"
            : "bg-transparent"
        }`}
        data-testid="navigation"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-20 md:h-24">

          {/* Logo */}
          <a
            href="#"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="flex items-center gap-0 group"
            data-testid="nav-logo"
          >
            <img
              src="/pirate-icon-transparent.png"
              alt="Pirate Bowling Center Logo"
              className="w-22 h-20 object-contain group-hover:scale-110 transition-transform duration-400"
            />
            <div className="-ml-1">
              <span className="font-unbounded text-sm font-bold text-cream tracking-widest uppercase block leading-none">
                Pirate
              </span>
              <span className="font-unbounded text-xs font-normal gold-text tracking-[0.2em] uppercase block leading-tight">
                Bowling Center
              </span>
            </div>
          </a>

          {/* Desktop Links */}
          <div className="hidden lg:flex items-center gap-4 xl:gap-8">
            {navLinks.map((link) => (
              <button
                key={link.label}
                onClick={() => goTo(link.href)}
                className="font-space-grotesk text-xs text-cream-muted hover:text-gold transition-colors duration-300 tracking-[0.1em] uppercase animated-underline"
                data-testid={`nav-link-${link.label.toLowerCase()}`}
              >
                {link.label}
              </button>
            ))}
          </div>

          {/* Desktop CTA */}
          <a
            href="https://4bowl.de/pirate-bowling/online/"
            target="_blank"
            rel="noopener noreferrer"
            className="hidden lg:block btn-gold px-5 py-2.5 rounded-full text-xs"
            data-testid="nav-cta-btn"
          >
            Reservieren
          </a>

          {/* Mobile Toggle */}
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="lg:hidden text-cream p-2 rounded-lg hover:bg-white/5 transition-colors"
            data-testid="nav-mobile-toggle"
            aria-label="Menü"
          >
            {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </motion.nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0, x: "100%" }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: "100%" }}
            transition={{ duration: 0.35, ease: "easeInOut" }}
            className="fixed inset-0 z-40 bg-navy-deep flex flex-col items-center justify-center gap-10"
            data-testid="nav-mobile-menu"
          >
            {/* Decorative ambient - removed blur orbs for mobile performance */}

            {/* Decorative gold line */}
            <motion.div
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="absolute top-20 left-1/2 -translate-x-1/2 w-32 h-px bg-gradient-to-r from-transparent via-gold/40 to-transparent"
            />

            {navLinks.map((link, i) => (
              <motion.button
                key={link.label}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15 + i * 0.12, ease: [0.25, 0.46, 0.45, 0.94] }}
                onClick={() => goTo(link.href)}
                className="relative font-unbounded text-2xl font-bold text-cream hover:text-gold transition-colors tracking-wider uppercase group"
                data-testid={`nav-mobile-link-${link.label.toLowerCase()}`}
              >
                {link.label}
                <span className="block h-px w-0 group-hover:w-full bg-gold transition-all duration-300 mt-1" />
              </motion.button>
            ))}
            <motion.a
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 + navLinks.length * 0.12 }}
              href="https://4bowl.de/pirate-bowling/online/"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-gold px-10 py-4 rounded-full text-sm"
              data-testid="nav-mobile-cta"
            >
              Bahn reservieren
            </motion.a>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
